import Alert from '@/components/Alert/Alert'
import UniversalWebView from '@/components/UniversalWebView'
import { UniversalWebViewRef } from '@/components/universalWebView/types'
import { getAuthToken } from '@/lib/api/authToken'
import { usePostKeyboardEventsToWebView } from '@/lib/hooks/usePostKeyboardEventsToWebView'
import { useSwitchTeam } from '@/lib/hooks/useSwitchTeam'
import { handleClickToPay } from '@/modules/handleClickToPay'
import { selectFormBillingAccount } from '@/redux/slices/formBillingAccountSlice'
import { openPublishSheet } from '@/redux/slices/publishSheetSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { PlanCode } from '@/typings/domain'
import { getCameraPermissionsAsync } from 'expo-image-picker'
import { router } from 'expo-router'
import { get } from 'lodash-es'
import { useCallback, useMemo, useRef } from 'react'

export default function FormEntries({
  formToken,
  searchParams,
}: {
  formToken: string
  searchParams?: Record<string, string>
}) {
  const dispatch = useAppDispatch()
  const switchTeam = useSwitchTeam()
  const currentBillingAccountId = useAppSelector(state => state.currentUser.user?.billingAccount.id)
  const formBillingAccount = useAppSelector(state => selectFormBillingAccount(state, formToken))
  const isCurrentUserExternal = formBillingAccount && currentBillingAccountId !== formBillingAccount.id
  const currentUser = useAppSelector(state => state.currentUser.user)
  const webviewRef = useRef<UniversalWebViewRef>(null)
  usePostKeyboardEventsToWebView(webviewRef)

  const uri = useMemo(() => {
    const token = getAuthToken()
    const base = `${process.env.EXPO_PUBLIC_NEXT_BASE_URL}/m/forms/${formToken}/entries?m_t=${token}`
    if (!searchParams || Object.keys(searchParams).length === 0) return base
    const extra = new URLSearchParams(searchParams).toString()
    return `${base}&${extra}`
  }, [formToken, searchParams])

  const handleMessage = useCallback(
    async (data: { event: string; details: unknown }) => {
      if (data.event === 'CLICK_TO_PAY') {
        if (isCurrentUserExternal) {
          Alert.alert('提示', '共享表单的此功能需要升级套餐，请联系创建者升级。')
          return
        }
        const recommendedPlanCode = get(data.details, 'recommendedPlanCode') as PlanCode | undefined
        handleClickToPay({ recommendedPlanCode, currentUser: currentUser ?? undefined })
        return
      }

      if (data.event === 'PUBLISH_FORM') {
        const formToken = get(data.details, 'formToken') as string | undefined
        if (formToken) {
          dispatch(openPublishSheet({ formToken }))
        }
        return
      }

      if (data.event === 'FORM_NOT_FOUND') {
        router.replace('/forms')
        return
      }

      if (data.event === 'SWITCH_TEAM') {
        const userId = get(data.details, 'userId') as string | undefined
        if (userId) {
          switchTeam(userId)
        } else {
          router.push('/profile/switchUser')
        }
        return
      }

      if (data.event === 'REQUEST_CAMERA_PERMISSION') {
        const { granted } = await getCameraPermissionsAsync()
        webviewRef.current?.postMessage(JSON.stringify({ event: 'CAMERA_PERMISSION_RESULT', details: { granted } }))
        return
      }
    },
    [currentUser, dispatch, isCurrentUserExternal, switchTeam]
  )

  return (
    <UniversalWebView
      ref={webviewRef}
      source={{ uri }}
      className="flex-1 bg-background-muted"
      allow="geolocation;camera;microphone"
      onMessage={handleMessage}
      onContentReady={() => {}}
      scrollEnabled={false}
    />
  )
}
